import {useCallback, useMemo, useState} from 'react'

import {useRouter} from 'next/router'
import {fromPairs, toInteger} from 'lodash'
import {useUpdateEffect, useDebounce} from 'react-use'
import {useRange} from 'react-instantsearch'

import {PriceRangeSlider} from '@festi/common/components/form'

import {ListItem} from './ChoiceRefinement'
import {priceRangeAttribute} from '../../utilites/refinement'

type RangeBoundaries = [number, number]

interface Props {
  items: ListItem[]
  onChange: (values: number) => void
}

export default function RangeRefinement({items, onChange}: Props) {
  const {query} = useRouter()

  const {range, start, refine} = useRange({
    attribute: priceRangeAttribute,
  })

  const [min, max] = start

  const [change, setChange] = useState<RangeBoundaries | []>([])
  const [minValue, setMinValue] = useState<number>(min || range.min)
  const [maxValue, setMaxValue] = useState<number>(max || range.max)

  const priceFrom = useMemo(
    () => (query?.priceFrom ? toInteger(query.priceFrom) : min),
    [query, min],
  )
  const priceTo = useMemo(
    () => (query?.priceTo ? toInteger(query.priceTo) : max),
    [max, query],
  )

  const hasPriceFrom = !min.toString().includes('Infinity')
  const hasPriceTo = !max.toString().includes('Infinity')

  const numberOfProductsPerPrice = fromPairs(
    items.map((item) => [item.value, item.count]),
  )

  const handleUpdateState = useCallback((values: RangeBoundaries) => {
    const [from, to] = values

    setMinValue(from)
    setMaxValue(to)
  }, [])

  const handleChange = useCallback(
    (values: RangeBoundaries) => {
      setChange(values)
      handleUpdateState(values)
    },
    [setChange, handleUpdateState],
  )

  useUpdateEffect(() => {
    if (!hasPriceFrom && !hasPriceTo) {
      handleUpdateState([range.min, range.max])
    }
  }, [range.min, range.max, priceFrom, priceTo])

  useDebounce(
    () => {
      const [from, to] = change

      !!change && refine([from, to])
    },
    300,
    [change],
  )

  useDebounce(
    () => {
      onChange((hasPriceFrom ? 1 : 0) + (hasPriceTo ? 1 : 0))
    },
    1000,
    [hasPriceFrom, hasPriceTo],
  )

  useDebounce(
    () => {
      if (priceFrom !== minValue || priceTo !== maxValue) {
        handleUpdateState([priceFrom, priceTo])
      }
    },
    1000,
    [priceFrom, priceTo],
  )

  return (
    <PriceRangeSlider
      min={range.min}
      max={range.max}
      data={numberOfProductsPerPrice}
      minValue={minValue < range.min ? range.min : minValue}
      maxValue={maxValue > range.max ? range.max : maxValue}
      onChange={handleChange}
    />
  )
}
